import { useState } from 'react';
import { categories, comics, type Comic } from '@/mocks/comics';
import ComicCard from './ComicCard';

const categoryKey: Record<string, string> = {
  热血: '热血',
  恋爱: '恋爱',
  玄幻: '玄幻',
  悬疑: '悬疑',
  搞笑: '搞笑',
  科幻: '科幻',
  校园: '校园',
  都市: '都市',
  动作: '动作',
  冒险: '冒险',
};

interface WorksSectionProps {
  keyword: string;
  onKeywordChange: (k: string) => void;
}

export default function WorksSection({ keyword, onKeywordChange }: WorksSectionProps) {
  const [activeCat, setActiveCat] = useState('全部');

  const filtered = comics.filter((c: Comic) => {
    const matchCat = activeCat === '全部' || c.category === activeCat;
    const kw = keyword.trim().toLowerCase();
    const matchKw =
      !kw ||
      c.title.toLowerCase().includes(kw) ||
      c.author.toLowerCase().includes(kw) ||
      c.tags.some((t) => t.toLowerCase().includes(kw));
    return matchCat && matchKw;
  });

  return (
    <section id="categories" className="w-full py-12 md:py-16 bg-background-50">
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-3">
              <i className="ri-grid-fill"></i>
              分类浏览
            </p>
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
              海量漫画，分类直达
            </h2>
            <p className="mt-2 text-foreground-600 text-sm">按类型快速筛选，从全品类漫画库中精准找到想看的作品。</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.name}
              type="button"
              onClick={() => setActiveCat(cat.name)}
              className={`group flex items-center gap-3 p-3 md:p-4 rounded-xl border text-left transition-colors ${
                activeCat === cat.name
                  ? 'bg-primary-500 border-primary-500 text-background-50'
                  : 'bg-background-100 border-background-200 hover:border-primary-200 hover:bg-primary-50'
              }`}
            >
              <span
                className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-lg ${
                  activeCat === cat.name ? 'bg-background-50/20 text-background-50' : 'bg-primary-100 text-primary-600'
                }`}
              >
                <i className={cat.icon}></i>
              </span>
              <span className="min-w-0">
                <span className="block font-medium text-sm">{cat.name}</span>
                <span
                  className={`block text-xs truncate ${activeCat === cat.name ? 'text-background-50/80' : 'text-foreground-500'}`}
                >
                  {cat.count}
                </span>
              </span>
            </button>
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {['全部', ...Object.values(categoryKey)].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCat(c)}
                className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                  activeCat === c
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <input
              type="text"
              value={keyword}
              onChange={(e) => onKeywordChange(e.target.value)}
              placeholder="在本页搜索作品…"
              className="w-full h-10 pl-10 pr-4 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition"
            />
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
            {filtered.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-500 text-sm">没有找到匹配的作品，试试更换关键词或分类。</p>
          </div>
        )}
      </div>
    </section>
  );
}